<template>
    <div class="container">
        <div class="title-container">
            <span>{{ $t('reclamation.do.title') }}</span>
            <ReglePastille cle="reclamation.definition" />
            <ReglePastille cle="reclamation.etapesObligatoires" />
        </div>
        <div class="content-container">
            <StepOne
                v-if="this.step === 1"
                :selectedSite="this.selectedSite"
                :title="$t('siteSelection.searchBar.title.request.intervention')"
                @selectSite="this.selectSite"
            />
            <StepTwo
                v-if="this.step === 2"
                :selectedSite="this.selectedSite"
                @description="handleDescriptionChange"
            />
        </div>
    </div>
    <BottomNavigation
        :back="back"
        :cancel="cancel"
        :confirm="request"
        :confirmButtonDisabled="!isValid()"
        :first="step === 1"
        :last="step === 2"
        :next="next"
    />
    <WaitingModal v-if="isLoading" :text="$t('intervention.request.pending')" />
    <SuccessModal v-if="success" :subtitle="$t('intervention.success.subtitle')" :title="response" @cancel="exit" />
</template>

<script lang="ts">
import StepOne from '@/modules/Reclamation/components/StepOne.vue';
import { defineComponent } from 'vue';
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import BottomNavigation from '@/components/layout/BottomNavigation.vue';
import { Site } from '@/models/Site';
import { SITE_LIST } from '@/modules/SiteList/store/mutation-types';
import StepTwo from '../components/StepTwo.vue';
import ReclamationService from '../services/ReclamationService';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import ErrorManager from '@/common/utils/ErrorManager';
import SuccessModal from '@/components/ui-components/SuccessModal.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    step: number;
    selectedSite: Site | null;
    description: string;
    isLoading: boolean;
    success: boolean;
    response: string;
}

export default defineComponent({
    name: 'ReclamationPage',
    components: {
        BottomNavigation,
        StepOne,
        StepTwo,
        SuccessModal,
        WaitingModal,
        ReglePastille,
    },
    data(): Data {
        return {
            step: 1,
            selectedSite: null,
            description: '',
            isLoading: false,
            success: false,
            response: '',
        };
    },
    methods: {
        cancel() {
            this.$router.replace('/dashboard');
            this.reset();
        },
        next() {
            this.step++;
        },
        back() {
            this.step--;
        },
        reset() {
            this.$store.commit(`siteList/${SITE_LIST.RESET}`);
            this.$store.commit(`siteList/${SITE_LIST.SET_FILTER}`, '');
        },
        exit() {
            this.reset();
            this.$router.replace('/dashboard');
        },
        selectSite(site: Site) {
            this.selectedSite = site;
        },
        isValid() {
            switch (this.step) {
                case 1:
                    return this.selectedSite;
                case 2:
                    return this.description.trim().length > 0;
            }
        },
        handleDescriptionChange(value: string) {
            this.description = value;
        },
        request() {
            this.isLoading = true;
            if (this.selectedSite && this.description) {
                ReclamationService.sendReclamation({
                    adresseSiteId: this.selectedSite.id,
                    chantierId: this.selectedSite.chantierId,
                    description: this.description,
                })
                    .then(async res => {
                        this.isLoading = false;
                        if (res.status === 200) {
                            this.success = true;
                            this.response = this.$t('reclamation.success.title', {
                                idReclamation: res.data,
                            });
                        } else
                            this.$store.commit(
                                `errorToaster/${ERROR.SET_MESSAGE}`,
                                await ErrorManager.processErrorCustomMessage(res.status, 'app.error.undefined'),
                                { root: true }
                            );
                    })
                    .catch(async error => {
                        this.$store.commit(
                            `errorToaster/${ERROR.SET_MESSAGE}`,
                            await ErrorManager.processError(error),
                            { root: true }
                        );
                        this.isLoading = false;
                    });
            }
        },
    },
    beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
        this.reset();
        next();
    },
});
</script>

<style scoped>
.container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 24px;
}
.title-container {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-family: 'Raleway Semi Bold', sans-serif !important;
    font-variant-numeric: lining-nums;
    margin-bottom: 8px;
}
</style>
